import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { RotateCcw, RotateCw, ArrowRight, ArrowLeft, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";

/**
 * iter207 → iter208 — Vendor ID crop sheet.
 *
 * Native browser canvas + touch events. No external crop library.
 *
 * iter208 changes:
 *   • **Pinch-to-zoom** (1× → 4×) + drag-to-pan on the image itself.
 *   • **Rotate is folded into the ID-crop step** — vendors rotate,
 *     zoom, and position the ID crop all on one screen. Face-crop
 *     step keeps the same layout (minus rotate buttons — rotation is
 *     already baked from step 1).
 *
 * Flow:
 *   1. `id`      — image with ID-aspect crop rect. ↺ ↻ rotate baked
 *                  on tap. Pinch + drag pan the image beneath the
 *                  fixed centered crop rectangle. "Next →".
 *   2. `face`    — same interaction, circle overlay (1:1). "Next →".
 *   3. `preview` — both crops side-by-side. "Submit".
 *   4. `submitting` — Cloudflare uploads via existing
 *                     `/applications/upload-id` + `/upload-avatar`.
 */

const ID_ASPECT = 1.586;   // ISO ID-1 (credit card / national ID)
const FACE_ASPECT = 1;
const MIN_ZOOM = 1;
const MAX_ZOOM = 4;

// ---------------------------------------------------------------- utilities

function loadImage(url) {
  return new Promise((res, rej) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = url;
  });
}

function fileToObjectUrl(file) {
  return URL.createObjectURL(file);
}

/** Rotate an image element by 90° increments; returns a new blob URL. */
async function bakeRotation(img, degrees) {
  const deg = ((degrees % 360) + 360) % 360;
  if (deg === 0) return null;
  const swap = deg === 90 || deg === 270;
  const canvas = document.createElement("canvas");
  canvas.width = swap ? img.naturalHeight : img.naturalWidth;
  canvas.height = swap ? img.naturalWidth : img.naturalHeight;
  const ctx = canvas.getContext("2d");
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((deg * Math.PI) / 180);
  ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
  return await new Promise((res) => canvas.toBlob((b) => res(URL.createObjectURL(b)), "image/jpeg", 0.92));
}

/** Extract a source-space crop rect into a fresh JPEG blob. */
async function extractCrop(img, rect, outW, outH) {
  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingQuality = "high";
  // Clamp source rect to image bounds so a slightly-out-of-bounds crop
  // doesn't draw transparent pixels.
  const sx = Math.max(0, Math.min(img.naturalWidth  - 1, rect.x));
  const sy = Math.max(0, Math.min(img.naturalHeight - 1, rect.y));
  const sw = Math.max(1, Math.min(img.naturalWidth  - sx, rect.w));
  const sh = Math.max(1, Math.min(img.naturalHeight - sy, rect.h));
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, outW, outH);
  return await new Promise((res) => canvas.toBlob((b) => res(b), "image/jpeg", 0.92));
}

// ---------------------------------------------------------------- zoomable crop

/**
 * ZoomableCrop — image is pan/zoomed with pointer + touch events; a
 * fixed centered crop rectangle sits above it. On every gesture we
 * recompute the crop rect in source-image pixel coords and publish
 * it to the parent via `onRectReady`.
 *
 * Pointer semantics:
 *   • 2 touches → pinch (adjust `scale`).
 *   • 1 touch / mouse drag → pan (adjust `pan.x`, `pan.y`).
 *   • Scroll wheel (desktop) → zoom.
 */
function ZoomableCrop({ imgUrl, aspect, shape = "rect", onRectReady }) {
  const wrapRef = useRef(null);
  const imgRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [natural, setNatural] = useState(null); // {nw, nh}
  const [wrap, setWrap] = useState(null);       // {W, H}
  const [cropDisp, setCropDisp] = useState(null); // {cw, ch} in wrapper px

  // Gesture state
  const pinchRef = useRef(null);
  const dragRef = useRef(null);

  const measure = () => {
    const w = wrapRef.current;
    const i = imgRef.current;
    if (!w || !i || !i.naturalWidth) return;
    setNatural({ nw: i.naturalWidth, nh: i.naturalHeight });
    setWrap({ W: w.clientWidth, H: w.clientHeight });
    // Centered aspect-locked crop = 85% of the smaller wrapper axis
    const cwMax = Math.min(w.clientWidth * 0.85, w.clientHeight * 0.85 * aspect);
    setCropDisp({ cw: cwMax, ch: cwMax / aspect });
    // Reset zoom & pan each time image (re-)loads.
    setScale(1); setPan({ x: 0, y: 0 });
  };

  useEffect(() => {
    const i = imgRef.current;
    if (i && i.complete && i.naturalWidth) measure();
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [imgUrl]);   

  // --- publish source-pixel crop rect whenever gesture state changes
  useEffect(() => {
    if (!natural || !wrap || !cropDisp) return;
    const { nw, nh } = natural;
    const { W, H } = wrap;
    const { cw, ch } = cropDisp;
    const s0 = Math.min(W / nw, H / nh);        // base contain scale
    const s  = s0 * scale;                       // effective scale
    const ew = s * nw, eh = s * nh;              // effective image dims
    // Image top-left in wrapper coords (centered + panned):
    const iLx = W / 2 + pan.x - ew / 2;
    const iLy = H / 2 + pan.y - eh / 2;
    // Crop top-left in wrapper coords:
    const cLx = W / 2 - cw / 2;
    const cLy = H / 2 - ch / 2;
    // Convert to source pixels:
    let sx = (cLx - iLx) / s;
    let sy = (cLy - iLy) / s;
    let sw = cw / s;
    let sh = ch / s;
    onRectReady({ x: sx, y: sy, w: sw, h: sh });
  }, [scale, pan, natural, wrap, cropDisp]);   

  // --- touch gestures ---
  const dist = (t1, t2) => Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);

  const clampPan = (p, scaleVal) => {
    if (!natural || !wrap) return p;
    const { nw, nh } = natural;
    const { W, H } = wrap;
    const s0 = Math.min(W / nw, H / nh);
    const s  = s0 * scaleVal;
    const ew = s * nw, eh = s * nh;
    // Constrain so at least the crop rect is covered by the image.
    const { cw, ch } = cropDisp || { cw: 0, ch: 0 };
    const maxPanX = (ew - cw) / 2;
    const maxPanY = (eh - ch) / 2;
    return {
      x: Math.max(-maxPanX, Math.min(maxPanX, p.x)),
      y: Math.max(-maxPanY, Math.min(maxPanY, p.y)),
    };
  };

  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      pinchRef.current = { startDist: dist(e.touches[0], e.touches[1]), startScale: scale };
      dragRef.current = null;
    } else if (e.touches.length === 1) {
      dragRef.current = { sx: e.touches[0].clientX, sy: e.touches[0].clientY, startPan: { ...pan } };
      pinchRef.current = null;
    }
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 2 && pinchRef.current) {
      const d = dist(e.touches[0], e.touches[1]);
      const next = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, pinchRef.current.startScale * (d / pinchRef.current.startDist)));
      setScale(next);
      setPan((p) => clampPan(p, next));
      e.preventDefault();
    } else if (e.touches.length === 1 && dragRef.current) {
      const nx = dragRef.current.startPan.x + (e.touches[0].clientX - dragRef.current.sx);
      const ny = dragRef.current.startPan.y + (e.touches[0].clientY - dragRef.current.sy);
      setPan(clampPan({ x: nx, y: ny }, scale));
      e.preventDefault();
    }
  };
  const onTouchEnd = (e) => {
    if (e.touches.length < 2) pinchRef.current = null;
    if (e.touches.length === 0) dragRef.current = null;
  };

  // Mouse fallback (desktop testing)
  const mouseRef = useRef(null);
  const onMouseDown = (e) => {
    mouseRef.current = { sx: e.clientX, sy: e.clientY, startPan: { ...pan } };
  };
  const onMouseMove = (e) => {
    if (!mouseRef.current) return;
    const nx = mouseRef.current.startPan.x + (e.clientX - mouseRef.current.sx);
    const ny = mouseRef.current.startPan.y + (e.clientY - mouseRef.current.sy);
    setPan(clampPan({ x: nx, y: ny }, scale));
  };
  const onMouseUp = () => { mouseRef.current = null; };
  const onWheel = (e) => {
    const next = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, scale - e.deltaY * 0.002));
    setScale(next);
    setPan((p) => clampPan(p, next));
    e.preventDefault();
  };

  const s0 = natural && wrap ? Math.min(wrap.W / natural.nw, wrap.H / natural.nh) : 1;

  return (
    <div
      ref={wrapRef}
      data-testid={`crop-drag-${shape}`}
      className="relative w-full h-full flex items-center justify-center overflow-hidden"
      style={{ background: "#000", touchAction: "none", cursor: "grab", userSelect: "none" }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
      onWheel={onWheel}
    >
      <img
        ref={imgRef}
        src={imgUrl}
        alt=""
        onLoad={measure}
        draggable={false}
        style={{
          maxWidth: "100%", maxHeight: "100%",
          objectFit: "contain",
          pointerEvents: "none",
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
          transformOrigin: "center center",
          transition: pinchRef.current || dragRef.current ? "none" : "transform 60ms linear",
        }}
      />
      {cropDisp && wrap && (
        <>
          {/* dim overlay outside crop */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              background: "rgba(0,0,0,0.55)",
              clipPath: shape === "circle"
                ? `path(evenodd, 'M0,0 H${wrap.W} V${wrap.H} H0 Z M${wrap.W / 2},${(wrap.H - cropDisp.ch) / 2} a${cropDisp.cw / 2},${cropDisp.ch / 2} 0 1,0 0.01,0 Z')`
                : `polygon(evenodd, 0 0, 100% 0, 100% 100%, 0 100%, 0 0, ${(((wrap.W - cropDisp.cw) / 2) / wrap.W) * 100}% ${(((wrap.H - cropDisp.ch) / 2) / wrap.H) * 100}%, ${(((wrap.W + cropDisp.cw) / 2) / wrap.W) * 100}% ${(((wrap.H - cropDisp.ch) / 2) / wrap.H) * 100}%, ${(((wrap.W + cropDisp.cw) / 2) / wrap.W) * 100}% ${(((wrap.H + cropDisp.ch) / 2) / wrap.H) * 100}%, ${(((wrap.W - cropDisp.cw) / 2) / wrap.W) * 100}% ${(((wrap.H + cropDisp.ch) / 2) / wrap.H) * 100}%, ${(((wrap.W - cropDisp.cw) / 2) / wrap.W) * 100}% ${(((wrap.H - cropDisp.ch) / 2) / wrap.H) * 100}%)`,
            }}
          />
          {/* crop frame border */}
          <div
            style={{
              position: "absolute",
              left: (wrap.W - cropDisp.cw) / 2,
              top:  (wrap.H - cropDisp.ch) / 2,
              width: cropDisp.cw,
              height: cropDisp.ch,
              borderRadius: shape === "circle" ? "50%" : 8,
              border: "2px solid #F5CC55",
              boxShadow: "0 0 0 1px rgba(0,0,0,0.4) inset",
              pointerEvents: "none",
            }}
          />
          {/* zoom indicator (tiny hint at bottom-right) */}
          <div
            data-testid="crop-zoom-indicator"
            style={{
              position: "absolute", right: 8, bottom: 8,
              padding: "2px 8px", borderRadius: 999,
              background: "rgba(0,0,0,0.55)", color: "#F5F1E8",
              fontFamily: "'DM Sans', sans-serif", fontSize: 12,
              pointerEvents: "none",
            }}
          >
            {scale.toFixed(1)}×
          </div>
          {/* Prevent unused-var lint (s0 is intentionally computed) */}
          <span aria-hidden="true" style={{ display: "none" }}>{s0.toFixed(2)}</span>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- main sheet

export default function IdCropSheet({ file, onCancel, onComplete }) {
  const { t } = useI18n();
  // iter208 — Combined rotate+id step; only 4 states now.
  const [step, setStep] = useState("id");    // id | face | preview | submitting
  const [srcUrl, setSrcUrl] = useState("");
  const [srcImg, setSrcImg] = useState(null);
  const [idRect, setIdRect] = useState(null);
  const [faceRect, setFaceRect] = useState(null);
  const [idBlobUrl, setIdBlobUrl] = useState("");
  const [faceBlobUrl, setFaceBlobUrl] = useState("");
  const [busy, setBusy] = useState(false);

  // File → initial source URL. `srcImg` is loaded lazily on the first
  // gesture that needs it (see `ensureSrcImg` below) so a slow decode
  // never surfaces as a phantom toast during the initial render.
  useEffect(() => {
    if (!file) return;
    const url = fileToObjectUrl(file);
    setSrcUrl(url);
    setSrcImg(null);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const ensureSrcImg = async () => {
    if (srcImg) return srcImg;
    try {
      const img = await loadImage(srcUrl);
      setSrcImg(img);
      return img;
    } catch {
      toast.error("Could not read image");
      return null;
    }
  };

  // ---------- inline rotate (iter208 combined step) -------------------------
  const rotate = async (deg) => {
    if (busy) return;
    setBusy(true);
    try {
      const img = await ensureSrcImg();
      if (!img) return;
      const newUrl = await bakeRotation(img, deg);
      if (!newUrl) return;                              // no-op (deg === 0)
      const newImg = await loadImage(newUrl);
      if (srcUrl && srcUrl.startsWith("blob:")) URL.revokeObjectURL(srcUrl);
      setSrcUrl(newUrl);
      setSrcImg(newImg);
    } finally {
      setBusy(false);
    }
  };

  // ---------- crop step advances -------------------------------------------
  const advanceIdCrop = async () => {
    const img = await ensureSrcImg();
    if (!img || !idRect) return;
    const blob = await extractCrop(img, idRect, 1200, Math.round(1200 / ID_ASPECT));
    setIdBlobUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(blob); });
    setStep("face");
  };

  const advanceFaceCrop = async () => {
    const img = await ensureSrcImg();
    if (!img || !faceRect) return;
    const blob = await extractCrop(img, faceRect, 640, 640);
    setFaceBlobUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(blob); });
    setStep("preview");
  };

  // ---------- submit --------------------------------------------------------
  const submit = async () => {
    setStep("submitting");
    try {
      const img = await ensureSrcImg();
      if (!img) { setStep("preview"); return; }
      const idBlob = await extractCrop(img, idRect, 1600, Math.round(1600 / ID_ASPECT));
      const faceBlob = await extractCrop(img, faceRect, 800, 800);

      const idForm = new FormData();
      idForm.append("file", idBlob, "id.jpg");
      const idRes = await api.post("/applications/upload-id", idForm, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const avForm = new FormData();
      avForm.append("file", faceBlob, "avatar.jpg");
      const avRes = await api.post("/applications/upload-avatar", avForm, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      onComplete?.({ id_url: idRes.data?.url, avatar_url: avRes.data?.url });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
      setStep("preview");
    }
  };

  // Cleanup on unmount
  useEffect(() => () => {
    if (idBlobUrl) URL.revokeObjectURL(idBlobUrl);
    if (faceBlobUrl) URL.revokeObjectURL(faceBlobUrl);
  }, []);   

  const stepEyebrow =
    step === "id"         ? t("crop_step_id_eyebrow") :
    step === "face"       ? t("crop_step_face_eyebrow") :
    step === "preview"    ? t("crop_step_preview") :
    step === "submitting" ? t("crop_step_submitting") : "";

  return createPortal((
    <div
      role="dialog"
      aria-modal="true"
      data-testid="id-crop-sheet"
      className="fixed inset-0 z-[70] flex flex-col"
      style={{ background: "#0B0B0B", height: "100dvh", maxHeight: "100dvh" }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 shrink-0"
           style={{ borderBottom: "1px solid #1f1f1f" }}>
        <button
          type="button"
          onClick={onCancel}
          data-testid="id-crop-cancel"
          style={{ background: "transparent", border: "none", color: "#F5F1E8",
                   padding: 8, cursor: "pointer", minHeight: 48, minWidth: 48 }}
        >
          <ArrowLeft className="w-5 h-5" strokeWidth={2} />
        </button>
        <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14,
                      color: "#9A9490", textTransform: "uppercase", letterSpacing: "1px" }}>
          {stepEyebrow}
        </div>
        <div style={{ width: 48 }} />
      </div>

      {/* Body */}
      <div
        className={`relative w-full ${step === "preview" || step === "submitting" ? "flex-1 min-h-0" : "shrink-0"}`}
        style={
          step === "preview" || step === "submitting"
            ? undefined
            : { height: "55dvh", maxHeight: "55dvh" }
        }
      >
        {step === "id" && srcUrl && (
          <ZoomableCrop
            imgUrl={srcUrl}
            aspect={ID_ASPECT}
            shape="rect"
            onRectReady={setIdRect}
          />
        )}
        {step === "face" && srcUrl && (
          <ZoomableCrop
            imgUrl={srcUrl}
            aspect={FACE_ASPECT}
            shape="circle"
            onRectReady={setFaceRect}
          />
        )}

        {step === "preview" && (
          <div data-testid="id-crop-preview" className="w-full h-full overflow-auto px-4 py-4">
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: "#F5F1E8" }}
                className="text-center mb-4">
              {t("crop_preview_title")}
            </h2>
            <div className="grid grid-cols-2 gap-3 max-w-md mx-auto">
              <div className="text-center">
                <div className="rounded-lg overflow-hidden"
                     style={{ background: "#141414", border: "1px solid #2a2a2a", aspectRatio: ID_ASPECT }}>
                  {idBlobUrl && <img src={idBlobUrl} alt="" className="w-full h-full object-cover" />}
                </div>
                <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, color: "#F5F1E8" }}
                     className="mt-2 font-semibold">
                  {t("crop_preview_id_label")}
                </div>
                <button
                  type="button"
                  onClick={() => setStep("id")}
                  data-testid="id-crop-redo-id"
                  style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, color: "#E8B84B",
                           minHeight: 48, background: "transparent", border: "none" }}
                >
                  {t("crop_preview_redo_id")}
                </button>
              </div>
              <div className="text-center">
                <div className="rounded-full overflow-hidden mx-auto"
                     style={{ width: 112, height: 112, background: "#141414", border: "1px solid #2a2a2a" }}>
                  {faceBlobUrl && <img src={faceBlobUrl} alt="" className="w-full h-full object-cover" />}
                </div>
                <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, color: "#F5F1E8" }}
                     className="mt-2 font-semibold">
                  {t("crop_preview_face_label")}
                </div>
                <button
                  type="button"
                  onClick={() => setStep("face")}
                  data-testid="id-crop-redo-face"
                  style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, color: "#E8B84B",
                           minHeight: 48, background: "transparent", border: "none" }}
                >
                  {t("crop_preview_redo_face")}
                </button>
              </div>
            </div>
          </div>
        )}

        {step === "submitting" && (
          <div className="w-full h-full flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: "#E8B84B" }} />
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 pt-2 shrink-0"
           style={{ background: "#171717", borderTop: "1px solid #1f1f1f",
                    paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 10px)" }}>

        {step === "id" && (
          <div className="text-center">
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22,
                          color: "#F5F1E8", lineHeight: 1.15 }} className="mb-1">
              {t("crop_step_id_title")}
            </div>
            <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16,
                          color: "#9A9490", lineHeight: 1.3 }} className="mb-2 truncate">
              {t("crop_step_id_sub")}
            </div>
            {/* iter208 — Rotate buttons live on the crop step now. */}
            <div className="flex justify-center gap-3 mb-2">
              <button
                type="button"
                onClick={() => rotate(-90)}
                data-testid="id-crop-rotate-left"
                disabled={busy}
                style={{
                  minWidth: 48, minHeight: 48, borderRadius: 8,
                  background: "#242015", border: "1px solid #3a3427", color: "#F5CC55",
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  cursor: busy ? "not-allowed" : "pointer",
                  opacity: busy ? 0.6 : 1,
                }}
                aria-label="Rotate left"
              >
                <RotateCcw className="w-5 h-5" strokeWidth={2} />
              </button>
              <button
                type="button"
                onClick={() => rotate(90)}
                data-testid="id-crop-rotate-right"
                disabled={busy}
                style={{
                  minWidth: 48, minHeight: 48, borderRadius: 8,
                  background: "#242015", border: "1px solid #3a3427", color: "#F5CC55",
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  cursor: busy ? "not-allowed" : "pointer",
                  opacity: busy ? 0.6 : 1,
                }}
                aria-label="Rotate right"
              >
                <RotateCw className="w-5 h-5" strokeWidth={2} />
              </button>
            </div>
            <PrimaryButton
              testid="id-crop-id-next"
              label={t("crop_next")}
              icon={<ArrowRight className="w-5 h-5" strokeWidth={2} />}
              onClick={advanceIdCrop}
            />
          </div>
        )}

        {step === "face" && (
          <div className="text-center">
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22,
                          color: "#F5F1E8", lineHeight: 1.15 }} className="mb-1">
              {t("crop_step_face_title")}
            </div>
            <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16,
                          color: "#9A9490", lineHeight: 1.3 }} className="mb-2 truncate">
              {t("crop_step_face_sub")}
            </div>
            <PrimaryButton
              testid="id-crop-face-next"
              label={t("crop_next")}
              icon={<ArrowRight className="w-5 h-5" strokeWidth={2} />}
              onClick={advanceFaceCrop}
            />
          </div>
        )}

        {step === "preview" && (
          <PrimaryButton
            testid="id-crop-submit"
            label={t("crop_submit")}
            onClick={submit}
            icon={<Check className="w-5 h-5" strokeWidth={2} />}
          />
        )}
      </div>
    </div>
  ), document.body);
}

// ---------------------------------------------------------------- small views

function PrimaryButton({ label, onClick, icon, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className="w-full flex items-center justify-center gap-2 rounded-lg transition-opacity"
      style={{
        minHeight: 48,
        background: "linear-gradient(135deg, #F5CC55 0%, #D4A843 100%)",
        color: "#1a0f06",
        fontFamily: "'DM Sans', sans-serif", fontSize: 16, fontWeight: 700,
        border: "none", cursor: "pointer",
      }}
    >
      {label} {icon}
    </button>
  );
}
